import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  TextField,
  ModalFooter,
  Button,
  Checkbox
} from '@deque/cauldron-react';
import ScrimmedLoader from '../../common/components/ScrimmedLoader';

export interface SendTestModalProps {
  showSendTestModal: boolean;
  onClose: () => void;
  handleSendTest: (testUnitId: string) => void;
  overrideTestDataFlag: boolean;
  setOverrideTestDataFlag: (value: boolean) => void;
  disableOverrideTestDataFlagCheckbox: boolean;
  messageToBeShown: string;
  showLoader: boolean;
}

export default function SendTestModal({
  showSendTestModal,
  onClose,
  handleSendTest,
  overrideTestDataFlag,
  setOverrideTestDataFlag,
  disableOverrideTestDataFlagCheckbox,
  messageToBeShown,
  showLoader
}: SendTestModalProps) {
  const { t } = useTranslation();
  const [testUnitId, setTestUnitId] = useState<string>('');
  const [testUnitIdError, setTestUnitIdError] = useState<string>('');
  const testUnitIdInputRef = useRef<HTMLInputElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disableOverrideTestDataFlagCheckbox) {
      checkboxRef.current?.focus();
    }
  }, [disableOverrideTestDataFlagCheckbox]);

  const onSend = (e: React.FormEvent) => {
    e.preventDefault();

    if (!testUnitId) {
      setTestUnitIdError(t(`Test Unit ID is required`));
      testUnitIdInputRef.current?.focus();
      return;
    }
    setTestUnitIdError('');
    handleSendTest(testUnitId);
  };

  const handleClose = () => {
    setTestUnitIdError('');
    setOverrideTestDataFlag(false);
    onClose();
  };

  return (
    <Modal
      show={showSendTestModal}
      heading={{ text: t('Send Test'), level: 2 }}
      onClose={handleClose}
    >
      <ModalContent>
        {showLoader && <ScrimmedLoader label={t('Sending...')} />}
        <form onSubmit={onSend}>
          <TextField
            label={t('Test Unit ID')}
            error={testUnitIdError}
            fieldRef={testUnitIdInputRef}
            type="input"
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTestUnitId(e.target.value)
            }
            value={testUnitId}
            required
          />

          {!disableOverrideTestDataFlagCheckbox && (
            <Checkbox
              disabled={disableOverrideTestDataFlagCheckbox}
              id={'overrideTestDataFlag'}
              checked={overrideTestDataFlag}
              onChange={e => setOverrideTestDataFlag(e.target.checked)}
              label={messageToBeShown}
              ref={checkboxRef}
            />
          )}
        </form>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onSend}>{t('Send')}</Button>
        <Button variant="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
